import { Modal, Typography } from 'antd';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../hooks/useAuth';
import { ROUTES } from '@shared/constants/routes';

const { Paragraph } = Typography;

/**
 * Blocking gate for users carrying `mustResetPassword` (e.g. freshly seeded /
 * invited admins). The BE returns `403 PASSWORD_RESET_REQUIRED` on every
 * non-whitelisted route until the password is changed, which otherwise leaves
 * the user staring at an empty dashboard with no explanation.
 *
 * This renders a non-dismissable popup on every screen EXCEPT the password
 * change screen (`/profile?tab=password`). Its single action routes the user
 * there. Once the password is changed, `setSession` clears the flag (see
 * ProfilePage PasswordTab) and the gate disappears.
 */
export function ForcePasswordResetGate() {
  const { t } = useTranslation('auth');
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [params] = useSearchParams();

  const mustReset = Boolean(user?.mustResetPassword);
  const onResetScreen =
    location.pathname === ROUTES.PROFILE && params.get('tab') === 'password';

  return (
    <Modal
      open={mustReset && !onResetScreen}
      closable={false}
      maskClosable={false}
      keyboard={false}
      title={t('forceReset.title')}
      okText={t('forceReset.cta')}
      cancelButtonProps={{ style: { display: 'none' } }}
      onOk={() => void navigate(`${ROUTES.PROFILE}?tab=password`)}
    >
      <Paragraph style={{ marginBottom: 0 }}>{t('forceReset.description')}</Paragraph>
    </Modal>
  );
}
